import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Button, Table, Empty, Spin, Typography, Tag, Space, message } from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
    fetchRecentQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v1/analytics/overview?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      message.error('Failed to load analytics data');
      console.error('Analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/analytics/questions?limit=50', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentQuestions(response.data);
    } catch (error) {
      console.error('Recent questions fetch error:', error);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v1/analytics/export?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `industrialiq-analytics-${dayjs().format('YYYY-MM-DD')}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Analytics exported successfully');
    } catch (error) {
      message.error('Failed to export analytics');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', paddingTop: '100px' }}>
        <Spin size="large" tip="Loading analytics..." />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: '24px' }}>
        <Empty description="No analytics data available" />
      </div>
    );
  }

  // Colors for charts
  const COLORS = ['#16A34A', '#D97706', '#DC2626'];
  const confidenceData = [
    { name: 'High (70-100%)', value: analytics.confidence_distribution.high, color: COLORS[0] },
    { name: 'Medium (40-69%)', value: analytics.confidence_distribution.medium, color: COLORS[1] },
    { name: 'Low (0-39%)', value: analytics.confidence_distribution.low, color: COLORS[2] }
  ];

  // Table columns for recent questions
  const columns = [
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      ellipsis: true,
      width: '40%',
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      render: (confidence) => (
        <Tag color={confidence >= 70 ? 'success' : confidence >= 40 ? 'warning' : 'error'}>
          {confidence}%
        </Tag>
      ),
      width: '15%',
    },
    {
      title: 'Tokens',
      dataIndex: 'tokens_used',
      key: 'tokens_used',
      render: (tokens) => tokens?.toLocaleString() || 0,
      width: '15%',
    },
    {
      title: 'Sources',
      dataIndex: 'sources_count',
      key: 'sources_count',
      width: '15%',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM D, YYYY'),
      width: '15%',
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Analytics Dashboard
          </Title>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            Insights into your document intelligence usage
          </Text>
        </div>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            Export Report
          </Button>
        </Space>
      </div>

      {/* Time Period Selector */}
      <Card style={{ marginBottom: '24px' }}>
        <Space>
          <Text strong>Time Period:</Text>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>Last 7 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>Last 30 Days</Button>
          <Button type={days === 90 ? 'primary' : 'default'} onClick={() => setDays(90)}>Last 90 Days</Button>
        </Space>
      </Card>

      {/* Key Metrics */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Questions"
              value={analytics.total_questions}
              prefix={<QuestionCircleOutlined />}
              valueStyle={{ color: '#0B3D91' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Average Confidence"
              value={analytics.average_confidence}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#16A34A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tokens Used"
              value={analytics.total_tokens}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#D97706' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Documents Queried"
              value={analytics.top_documents.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#0B3D91' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1: Questions and Token Trends */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title={<><LineChartOutlined /> Question Activity</>} bordered={false}>
            {analytics.daily_stats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.daily_stats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="questions" stroke="#0B3D91" strokeWidth={2} name="Questions" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No data available" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><BarChartOutlined /> Token Usage Trends</>} bordered={false}>
            {analytics.daily_stats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.daily_stats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tokens" fill="#D97706" name="Tokens Used" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No data available" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2: Confidence Distribution and Top Documents */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title={<><PieChartOutlined /> Confidence Distribution</>} bordered={false}>
            {confidenceData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={confidenceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {confidenceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No confidence data available" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><FileTextOutlined /> Most Queried Documents</>} bordered={false}>
            {analytics.top_documents.length > 0 ? (
              <div style={{ height: '300px', overflow: 'auto' }}>
                <Table
                  dataSource={analytics.top_documents}
                  columns={[
                    { title: 'Document', dataIndex: 'document', key: 'document', ellipsis: true },
                    { title: 'Queries', dataIndex: 'usage_count', key: 'usage_count', width: '100px' },
                  ]}
                  pagination={false}
                  size="small"
                  rowKey="document"
                />
              </div>
            ) : (
              <Empty description="No document usage data" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Questions Table */}
      <Card title={<><QuestionCircleOutlined /> Recent Questions</>} bordered={false}>
        <Table
          dataSource={recentQuestions}
          columns={columns}
          pagination={{ pageSize: 10 }}
          size="small"
          rowKey="id"
        />
      </Card>
    </div>
  );
}

export default Analytics;
